import type { CaptureResult, Properties } from "posthog-js";
import {
  isPublicProfilePathname,
  maskPublicProfilePathname,
  PUBLIC_PROFILE_EVENT_NAMES,
} from "@/domains/freelance/profile/constants/public-profile-events";

/**
 * I parametri di campagna che posthog-js classifica come **dati personali**
 * (`PERSONAL_DATA_CAMPAIGN_PARAMS`): identificatori di click pubblicitari, che
 * seguono la persona da un sito all'altro. Con `save_campaign_params` di
 * default la libreria li legge dalla query dell'URL e li allega a **ogni**
 * evento, ripetuti in `$session_entry_<nome>` e in `$initial_<nome>`.
 *
 * Lo scenario primario del FLOW è proprio un link aperto da Messenger o
 * LinkedIn: `?fbclid=…` in coda all'URL. È «altro dato del visitatore» (AC16)
 * nel senso più forte.
 *
 * L'elenco è pinnato contro la libreria installata in
 * `posthog-scrub.integration.test.tsx`: se posthog-js ne aggiunge uno, il test
 * arrossisce. Gli `utm_*` restano: sono la campagna, non la persona.
 */
export const PERSONAL_DATA_CAMPAIGN_PARAMS = [
  "gclid",
  "gclsrc",
  "dclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "msclkid",
  "twclid",
  "li_fat_id",
  "igshid",
  "ttclid",
  "rdt_cid",
  "epik",
  "qclid",
  "sccid",
  "irclid",
  "_kx",
] as const;

/**
 * Le proprietà automatiche che descrivono il **visitatore** e non la pagina:
 * il referrer intero (l'URL da cui è arrivato, con query e frammento — cioè il
 * thread della conversazione), nelle tre copie che posthog-js ne tiene
 * (`$referrer` a ogni evento, `$session_entry_referrer` per la sessione,
 * `$initial_referrer` e `$initial_person_info` in `$set_once` per la persona),
 * e lo user agent grezzo. `$referring_domain` resta: dice "da LinkedIn" o "da
 * WhatsApp", che è l'unica cosa che serve sapere su un link condiviso, senza
 * l'URL della conversazione da cui è partito.
 */
export const VISITOR_PROPERTIES = [
  "$referrer",
  "$session_entry_referrer",
  "$initial_referrer",
  "$initial_person_info",
  "$raw_user_agent",
] as const;

/**
 * Le proprietà che portano un **URL intero**, query compresa: la query è dove
 * vivono `fbclid` & co. una seconda volta. Ridotte a origine + percorso.
 */
export const URL_PROPERTIES = [
  "$current_url",
  "$session_entry_url",
  "$initial_current_url",
] as const;

/**
 * posthog-js ripete ogni parametro di campagna sotto due prefissi:
 * `$session_entry_<nome>` a ogni evento e `$initial_<nome>` in `$set_once`.
 */
const withPrefixes = (name: string) => [
  name,
  `$session_entry_${name}`,
  `$initial_${name}`,
];

const REMOVED: ReadonlySet<string> = new Set([
  ...VISITOR_PROPERTIES,
  ...PERSONAL_DATA_CAMPAIGN_PARAMS.flatMap(withPrefixes),
]);
const TRUNCATED: ReadonlySet<string> = new Set(URL_PROPERTIES);

/** `https://app.sevedemo.it/profile/x?fbclid=…#f` → `https://app.sevedemo.it/profile/x`. */
const stripQuery = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  const cut = value.search(/[?#]/);
  return cut === -1 ? value : value.slice(0, cut);
};

/**
 * `https://app.sevedemo.it/profile/giulia-ferrante` →
 * `https://app.sevedemo.it/profile/:segment`.
 *
 * La query è già stata tolta da `stripQuery`; qui cade il **percorso**, che
 * da quando il link porta lo username contiene nome e cognome di una persona.
 * Un URL che non si sa leggere resta com'è: è già passato dallo scrub della
 * query, e inventarsi una maschera su una stringa non-URL sarebbe peggio.
 */
const maskProfileUrl = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  try {
    const url = new URL(value);
    return isPublicProfilePathname(url.pathname)
      ? `${url.origin}${maskPublicProfilePathname(url.pathname)}`
      : value;
  } catch {
    return value;
  }
};

const isRecord = (value: unknown): value is Properties =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const scrubProperties = (properties: Properties): Properties => {
  const out: Properties = {};
  for (const [key, value] of Object.entries(properties)) {
    if (REMOVED.has(key)) continue;
    if (TRUNCATED.has(key)) {
      out[key] = maskProfileUrl(stripQuery(value));
      continue;
    }
    if (key === "$pathname" && typeof value === "string") {
      out[key] = maskPublicProfilePathname(value);
      continue;
    }
    // `$set` / `$set_once`: le stesse chiavi, un livello sotto.
    if ((key === "$set" || key === "$set_once") && isRecord(value)) {
      out[key] = scrubProperties(value);
      continue;
    }
    out[key] = value;
  }
  return out;
};

/**
 * Un evento va scremato se è uno dei tre dichiarati, **oppure** se è stato
 * emesso sulla pagina pubblica: il `$pageview` di `TrackerProvider` e gli
 * eventi di autocapture arrivano dalla stessa apertura, con lo stesso
 * referrer, sotto un altro nome. `$pathname` lo allega posthog-js da
 * `window.location`, non il call site.
 */
const shouldScrub = (event: CaptureResult): boolean =>
  PUBLIC_PROFILE_EVENT_NAMES.has(event.event) ||
  isPublicProfilePathname(event.properties?.$pathname);

/**
 * `before_send` della pagina pubblica: AC16 promette che nessun evento porti
 * «referrer completo o altri dati del visitatore», ma posthog-js li allega da
 * solo a **ogni** `capture`, quindi la promessa va mantenuta qui, all'ultimo
 * passaggio prima della rete, non al call site.
 *
 * Tolti: referrer intero e user agent grezzo; i 17 identificatori pubblicitari
 * di `PERSONAL_DATA_CAMPAIGN_PARAMS`, in ogni prefisso; la query degli URL.
 * Restano `$referring_domain`, gli `utm_*`, `$pathname` e tutto ciò che
 * descrive il browser in aggregato. Gli eventi di ogni altra pagina passano
 * intatti: un `before_send` che toccasse eventi non suoi cambierebbe il
 * significato di ogni altra metrica dell'app.
 *
 * Fuori portata, per costruzione: `$ip` e la geolocalizzazione le calcola il
 * server dall'indirizzo della richiesta (toggle di progetto «Discard client IP
 * data»), e il `distinct_id` anonimo che `identify` fonde alla registrazione.
 * Entrambi a verbale nella mappa tracking.
 */
export const scrubPublicProfileEvent = (
  event: CaptureResult | null,
): CaptureResult | null => {
  if (!event || !shouldScrub(event)) return event;
  return { ...event, properties: scrubProperties(event.properties ?? {}) };
};
